import React, { useRef } from 'react';
import { ChangeEvent, DragEvent, SyntheticEvent, useState } from 'react';
import FileUploadAlert from './FileUploadAlert';
import { FileUploadProps } from './FileUpload.types';
import './FileUpload.scss';
import { ValidationError } from '../ValidationError';

export const FileUpload = (props: FileUploadProps) => {
  const fileInputRef = useRef();
  const {
    acceptedMimeTypes,
    acceptedFileCount,
    acceptedFileSizeLimit,
    onFileListUpdated,
    id = 'tfl-file-upload',
    errorMessage = '',
    showError = false,
    label,
    required,
    inputRef = fileInputRef,
    onChange,
    uploadInProgress = false,
    displaySuccessMessage = true,
    ...rest
  } = props;

  if (acceptedFileCount < 1) {
    throw RangeError('The prop "acceptedFileCount" must be greater than 0.');
  }

  if (acceptedFileSizeLimit < 1) {
    throw RangeError(
      'The prop "acceptedFileSizeLimit" must be greater than 1 byte'
    );
  }

  const [errorAlerts, setErrorAlerts] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const oneKiloByte = 1024;
  const oneMegaByte = 1048576;

  function formatFileSize(number) {
    if (number < oneKiloByte) {
      return `${number} B`;
    } else if (number >= oneKiloByte && number < oneMegaByte) {
      return `${(number / oneKiloByte).toFixed(1)} KB`;
    } else if (number >= oneMegaByte) {
      return `${(number / oneMegaByte).toFixed(1)} MB`;
    }
  }

  const handleOnChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
    handleFiles(event.target.files);
  };

  enum DragEventType {
    Enter,
    Over,
    Leave,
    Drop,
  }

  const handleDragEvent = (
    event: DragEvent<HTMLDivElement>,
    type: DragEventType
  ) => {
    if (uploadInProgress) return;
    event.preventDefault();
    event.stopPropagation();
    if (type === DragEventType.Enter || type === DragEventType.Over) {
      event.currentTarget.classList.add('tfl-file-input__dropzone--active');
    }

    if (type === DragEventType.Leave) {
      event.currentTarget.classList.remove('tfl-file-input__dropzone--active');
    }

    if (type === DragEventType.Drop) {
      event.currentTarget.classList.remove('tfl-file-input__dropzone--active');
      event.dataTransfer.files.length && handleFiles(event.dataTransfer.files);
    }
  };

  const handleFiles = (fileList: FileList | null) => {
    const addedFiles = [...(fileList as unknown as File[])];
    const errors: string[] = [];

    if (!addedFiles) {
      errors.push('Select a file');
      setErrorAlerts(errors);
      return;
    }

    if (addedFiles.length + files.length > acceptedFileCount) {
      errors.push(
        `${
          acceptedFileCount == 1
            ? 'Select no more than 1 file'
            : `Select no more than ${acceptedFileCount} files`
        }`
      );
      setErrorAlerts(errors);
      return;
    }

    for (const file of addedFiles) {
      if (file.size > acceptedFileSizeLimit) {
        errors.push(
          `There was an error uploading ${
            file.name
          }. File size exceeds ${formatFileSize(acceptedFileSizeLimit)}`
        );
      }

      if (file.name.length > 100) {
        errors.push(
          `There was an error uploading ${file.name}. File name size exceeds 100 characters limit`
        );
      }

      if (!acceptedMimeTypes.some((type) => type === file.type)) {
        errors.push(
          `There was an error uploading ${file.name}. File type is not allowed`
        );
      }
    }

    if (errors.length) {
      setErrorAlerts(errors);
      return;
    }
    const newFileList = [...files, ...addedFiles];
    setErrorAlerts([]);
    setFiles(newFileList);

    if (!onFileListUpdated) return;

    onFileListUpdated(newFileList);
  };

  const fileClick = (e: SyntheticEvent<HTMLElement>) => {
    e.preventDefault();
  };

  const removeFileAtIndex = (
    e: SyntheticEvent<HTMLElement>,
    fileIndex: number
  ) => {
    e.preventDefault();
    setErrorAlerts([]);
    if (files === null) return;

    const remainingFiles = files.filter((_, index) => index !== fileIndex);
    setFiles(() => [...remainingFiles]);
    if (inputRef.current) inputRef.current.value = null;
    if (!onFileListUpdated) return;

    onFileListUpdated(remainingFiles);
  };

  return (
    <div className="tfl-file-input">
      <label htmlFor={id} className="tfl-file-input__label">
        {label}
        {!required && (
          <span className="tfl-file-input__optional"> (optional)</span>
        )}
      </label>
      <div className="tfl-file-input__alerts">
        {files &&
          files.length > 0 &&
          displaySuccessMessage &&
          files.map((file, index) => (
            <FileUploadAlert
              alertStyle="success"
              key={`${file.name}-${index}`}
              header={`${file.name} (${formatFileSize(file.size)})`}
              isDismisable
              onDismissButtonClick={(e) => removeFileAtIndex(e, index)}
              show
            />
          ))}
        {errorAlerts &&
          errorAlerts.map((error) => (
            <FileUploadAlert
              alertStyle="error"
              key={error}
              header={error}
              show
            />
          ))}
      </div>
      <div
        className={`${
          uploadInProgress
            ? 'tfl-file-input__upload-in-progress tfl-file-input__dropzone'
            : 'tfl-file-input__dropzone'
        }`}
        onClick={() => {
          document.getElementById(id).click();
        }}
        onDragEnter={(e) => handleDragEvent(e, DragEventType.Enter)}
        onDragOver={(e) => handleDragEvent(e, DragEventType.Over)}
        onDragLeave={(e) => handleDragEvent(e, DragEventType.Leave)}
        onDrop={(e) => handleDragEvent(e, DragEventType.Drop)}
        data-testid="file-input-drop-zone"
      >
        {uploadInProgress === false ? (
          <span className="tfl-file-input__action-text">
            Drag file here to upload or{' '}
            <a
              href="#"
              className="tfl-file-input__action-text-highlighted"
              onClick={fileClick}
            >
              browse files on device
            </a>
          </span>
        ) : (
          <div className="inprogress-banner">
            File upload in progress, please wait
          </div>
        )}
      </div>
      <input
        disabled={uploadInProgress}
        className="tfl-file-input__input"
        ref={inputRef}
        onChange={handleOnChangeEvent}
        id={id}
        type="file"
        multiple={acceptedFileCount > 1}
        {...rest}
      />
      <ValidationError
        errorMessage={errorMessage}
        showErrorMessage={showError && errorMessage !== ''}
      />
    </div>
  );
};
